import { useState, useRef } from 'react'
import { MoreVertical, Copy, FolderInput, Trash2, Check } from 'lucide-react'
import { type AdminAsset } from '../lib/api'
import { normalizeImageKitUrl } from '@lic/media-urls'

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface AssetGridProps {
  assets: AdminAsset[]
  mode: 'page' | 'picker'
  pickerSelectedId: string | null
  pageSelectedIds: Set<string>
  onAssetClick: (asset: AdminAsset) => void
  onOpenDetail?: (asset: AdminAsset) => void
  onUploadFile: (file: File) => void
  onMoveRequest: (assetIds: string[]) => void
  onDeleteRequest: (assetId: string) => void
  onCopyUrl: (url: string) => void
  onSelectionChange: (ids: Set<string>) => void
}

function ContextMenu({
  onMove,
  onCopy,
  onDelete,
  onClose,
}: {
  asset: AdminAsset
  onMove: () => void
  onCopy: () => void
  onDelete: () => void
  onClose: () => void
  anchorRef: React.RefObject<HTMLButtonElement>
}) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute right-0 top-7 z-50 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl overflow-hidden w-36 text-xs">
        <button type="button" onClick={() => { onCopy(); onClose() }}
          className="flex items-center gap-2 w-full px-3 py-2 text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors">
          <Copy size={12} /> Copy URL
        </button>
        <button type="button" onClick={() => { onMove(); onClose() }}
          className="flex items-center gap-2 w-full px-3 py-2 text-zinc-300 hover:bg-zinc-700 hover:text-white transition-colors">
          <FolderInput size={12} /> Move to…
        </button>
        <button type="button" onClick={() => { onDelete(); onClose() }}
          className="flex items-center gap-2 w-full px-3 py-2 text-red-400 hover:bg-red-900/30 hover:text-red-300 transition-colors">
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </>
  )
}

function AssetCard({
  asset,
  mode,
  isPickerSelected,
  isPageSelected,
  anyPageSelected,
  onAssetClick,
  onOpenDetail,
  onMoveRequest,
  onDeleteRequest,
  onCopyUrl,
  onCheckboxChange,
}: {
  asset: AdminAsset
  mode: 'page' | 'picker'
  isPickerSelected: boolean
  isPageSelected: boolean
  anyPageSelected: boolean
  onAssetClick: (asset: AdminAsset) => void
  onOpenDetail?: (asset: AdminAsset) => void
  onMoveRequest: (assetIds: string[]) => void
  onDeleteRequest: (assetId: string) => void
  onCopyUrl: (url: string) => void
  onCheckboxChange: (id: string, checked: boolean) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuBtnRef = useRef<HTMLButtonElement>(null!)

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('assetId', asset.id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const showCheckbox = mode === 'page' && (anyPageSelected || isPageSelected)
  const isSelected = mode === 'picker' ? isPickerSelected : isPageSelected
  const borderClass = isSelected
    ? 'border-yellow-400'
    : 'border-zinc-700 hover:border-zinc-500'

  return (
    <div
      draggable={mode === 'page'}
      onDragStart={handleDragStart}
      className={`relative group bg-zinc-800 rounded-lg border-2 overflow-hidden transition-colors ${borderClass}`}
    >
      {/* Checkbox (page mode only) */}
      {mode === 'page' && (
        <div className={`absolute top-1.5 left-1.5 z-10 transition-opacity ${showCheckbox ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          <input
            type="checkbox"
            checked={isPageSelected}
            onChange={(e) => onCheckboxChange(asset.id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 rounded accent-yellow-400 cursor-pointer"
          />
        </div>
      )}

      {/* Picker selected indicator */}
      {mode === 'picker' && isPickerSelected && (
        <div className="absolute top-1.5 right-1.5 z-10 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
          <Check size={11} strokeWidth={3} className="text-black" />
        </div>
      )}

      {/* 3-dot menu (page mode only) */}
      {mode === 'page' && (
        <div className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="relative">
            <button type="button"
              ref={menuBtnRef}
              onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o) }}
              className="bg-black/50 hover:bg-black/70 text-white rounded p-0.5 transition-colors"
            >
              <MoreVertical size={13} />
            </button>
            {menuOpen && (
              <ContextMenu
                asset={asset}
                anchorRef={menuBtnRef}
                onMove={() => onMoveRequest([asset.id])}
                onCopy={() => onCopyUrl(asset.url)}
                onDelete={() => onDeleteRequest(asset.id)}
                onClose={() => setMenuOpen(false)}
              />
            )}
          </div>
        </div>
      )}

      <img
        src={normalizeImageKitUrl(asset.url)}
        alt={asset.alt || asset.filename}
        onClick={() => mode === 'picker' ? onAssetClick(asset) : onOpenDetail?.(asset)}
        className="w-full aspect-square object-cover bg-zinc-900 cursor-pointer"
        onError={(e) => { (e.target as HTMLImageElement).style.background = '#18181b' }}
      />
      <div className="p-2 space-y-0.5" onClick={() => mode === 'picker' ? onAssetClick(asset) : onOpenDetail?.(asset)}>
        <p className="text-zinc-300 text-xs truncate cursor-pointer">{asset.filename}</p>
        <p className="text-zinc-500 text-[10px]">{formatSize(asset.size)}</p>
      </div>
    </div>
  )
}

export default function AssetGrid({
  assets,
  mode,
  pickerSelectedId,
  pageSelectedIds,
  onAssetClick,
  onOpenDetail,
  onUploadFile,
  onMoveRequest,
  onDeleteRequest,
  onCopyUrl,
  onSelectionChange,
}: AssetGridProps) {
  const [gridDragOver, setGridDragOver] = useState(false)

  const handleGridDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setGridDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) {
      onUploadFile(file)
    }
    // asset card drags are handled by FolderSidebar drop targets
  }

  const handleCheckboxChange = (id: string, checked: boolean) => {
    const next = new Set(pageSelectedIds)
    if (checked) next.add(id)
    else next.delete(id)
    onSelectionChange(next)
  }

  if (assets.length === 0) {
    return (
      <div
        onDragOver={(e) => { e.preventDefault(); setGridDragOver(true) }}
        onDragLeave={() => setGridDragOver(false)}
        onDrop={handleGridDrop}
        className={`flex-1 flex flex-col items-center justify-center gap-3 py-24 rounded-lg border-2 border-dashed transition-colors ${
          gridDragOver ? 'border-yellow-400 bg-yellow-400/5' : 'border-zinc-800'
        }`}
      >
        <p className="text-zinc-500 text-sm">Folder ini kosong.</p>
        <p className="text-zinc-600 text-xs">Drag files here or use Upload.</p>
      </div>
    )
  }

  return (
    <div
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setGridDragOver(true) }
      }}
      onDragLeave={() => setGridDragOver(false)}
      onDrop={handleGridDrop}
      className={`flex-1 overflow-y-auto rounded-lg transition-colors ${
        gridDragOver ? 'ring-2 ring-yellow-400 bg-yellow-400/5' : ''
      }`}
    >
      <div className={`grid gap-3 ${mode === 'picker' ? 'grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5'}`}>
        {assets.map((asset) => (
          <AssetCard
            key={asset.id}
            asset={asset}
            mode={mode}
            isPickerSelected={pickerSelectedId === asset.id}
            isPageSelected={pageSelectedIds.has(asset.id)}
            anyPageSelected={pageSelectedIds.size > 0}
            onAssetClick={onAssetClick}
            onOpenDetail={onOpenDetail}
            onMoveRequest={onMoveRequest}
            onDeleteRequest={onDeleteRequest}
            onCopyUrl={onCopyUrl}
            onCheckboxChange={handleCheckboxChange}
          />
        ))}
      </div>
    </div>
  )
}
